import type { DataSourceFamily, SyncCapability } from "@shared/utils/crmTypes";

import { crmTypeInfo, isIngestionCrmType } from "@shared/utils/crmTypes";
import { CUSTOM_SYNCER_LABELS, isCustomSyncerKey } from "@shared/utils/customSyncerKeys";

import type { ReviewItem, SourceSummary } from "@/api/admin/ingestion";

export type { DataSourceFamily } from "@shared/utils/crmTypes";

export interface DataSourceCapabilities {
  hasReview: boolean;
  sync: SyncCapability;
  usesToken: boolean;
}

export interface DataSourceHealth {
  detail?: string;
  lastActivityAt: null | string;
  status: "failed" | "idle" | "ok" | "warning";
}

export interface DataSource {
  capabilities: DataSourceCapabilities;
  // Live cases from this source — the "cases" figure shown on the list. For ingestion
  // families it's the distinct count from /sources (deduped across re-pulls); for pull CRMs
  // it's the sum of their campaigns' totalCases.
  caseCount: number;
  // When the source was added (ISO). Ingestion sources only; pull CRMs report null
  // (the crm-configs list doesn't surface a creation timestamp).
  createdAt: null | string;
  crmConfigId: string;
  crmType: string;
  // Records that failed to import. A SEPARATE concern from mapping (the fix is retry/inspect,
  // not a mapping choice) — surfaced as its own notification, never folded into `issueCount`.
  failureCount: number;
  family: DataSourceFamily;
  health: DataSourceHealth;
  // Pending decisions that need a human choice: new columns to map, new routing values to
  // assign, new data formats to confirm. EXCLUDES failures. Drives the "Map N items" button,
  // the overview banner, and the sidebar badge — so the number always equals what the
  // resolve-issues flow shows.
  issueCount: number;
  mappedCampaignCount: number;
  name: string;
  organizationId: string;
  organizationName: string;
  // Cumulative records RECEIVED through this source (append-per-payload, so re-pulls inflate
  // it past caseCount). Kept for observability; NOT shown as "cases". Pull CRMs report 0.
  recordCount: number;
  // Scheduled sync interval in hours; null = once a day (RAP-613). Pull CRMs only.
  syncIntervalHours: null | number;
}

// All per-crmType facts (family, sync capability, label) come from the shared
// CRM_TYPE_INFO map — these helpers are thin derivations kept for their call sites.

export function familyFor(crmType: string): DataSourceFamily {
  return crmTypeInfo(crmType).family;
}

export function capabilitiesFor(crmType: string): DataSourceCapabilities {
  const { family, sync } = crmTypeInfo(crmType);
  // Ingestion sources (push/spreadsheet) have a review inbox and an API token; pull CRMs don't.
  const ingestion = family !== "PULL";
  return { hasReview: ingestion, sync, usesToken: ingestion };
}

export function humanizeCrmType(crmType: string): string {
  return crmTypeInfo(crmType).label;
}

/**
 * Display name for a pull CRM that carries no `name` of its own (the /admin/crm-configs
 * list doesn't return one, and pull configs are null in the DB).
 *
 * Custom syncers all share crmType "CUSTOM", so the humanized-crmType fallback rendered
 * the bare enum in the Source column while the Type badge said "CUSTOM" too — the row
 * never named the integration. Resolve the registered label instead; an unknown or
 * missing key keeps the crmType fallback so the column can never render blank.
 */
function pullSourceName(c: RawCrmConfig): string {
  if (c.crmType === "CUSTOM" && isCustomSyncerKey(c.customSyncerKey)) {
    return CUSTOM_SYNCER_LABELS[c.customSyncerKey];
  }
  return humanizeCrmType(c.crmType);
}

// Minimal shape we read from GET /admin/crm-configs (loosely typed in adminStore).
export interface RawCrmConfig {
  createdAt?: null | string;
  crmType: string;
  // Set only when crmType === "CUSTOM" — identifies which custom syncer this is.
  customSyncerKey?: null | string;
  id: string;
  lastSyncedAt?: null | string;
  name?: null | string;
  organizationId: string;
  organizationName?: null | string;
  syncIntervalHours?: null | number;
  syncStatus?: null | string;
}

// Minimal shape we read from the admin campaigns list (AdminCampaign).
export interface RawCampaignLink {
  crmConfigId?: null | string;
  lastSyncedAt?: null | string;
  totalCases?: number;
}

// A pull CRM's sync recency lives on its CAMPAIGNS (Campaign.lastSyncedAt), NOT on
// CrmConfig.lastSyncedAt — the sync pipeline only updates the per-campaign field, and
// the /admin/crm-configs list doesn't even return name/syncStatus. So health is derived
// from the latest campaign sync for this config.
function pullHealth(c: RawCrmConfig, latestCampaignSync: null | string): DataSourceHealth {
  if (c.syncStatus && /fail|error/i.test(c.syncStatus)) {
    return {
      detail: "Sync failed",
      lastActivityAt: latestCampaignSync ?? c.lastSyncedAt ?? null,
      status: "failed",
    };
  }
  const last = latestCampaignSync ?? c.lastSyncedAt ?? null;
  if (!last) return { detail: "Never synced", lastActivityAt: null, status: "idle" };
  return { lastActivityAt: last, status: "ok" };
}

function ingestionHealth(s: SourceSummary): DataSourceHealth {
  if (!s.lastReceivedAt)
    return { detail: "No data received yet", lastActivityAt: null, status: "idle" };
  return { lastActivityAt: s.lastReceivedAt, status: "ok" };
}

// Split a source's pending review work into ISSUES (decisions a human resolves: new columns,
// routing values, data formats) vs FAILURES (records that errored). Failures are a separate
// notification, so they're tallied apart and never counted as "items to map".
function reviewCountsFor(
  crmConfigId: string,
  rows: ReviewItem[]
): { failures: number; issues: number } {
  let issues = 0;
  let failures = 0;
  for (const r of rows) {
    if (r.crmConfigId !== crmConfigId) continue;
    issues += r.structureCount + r.fieldCount + r.routingCount;
    failures += r.failureCount;
  }
  return { failures, issues };
}

/** Merge pull CRMs + ingestion sources into one normalized list. */
export function normalizeDataSources(
  crmConfigs: RawCrmConfig[],
  ingestionSources: SourceSummary[],
  reviewByCampaign: ReviewItem[],
  campaigns: RawCampaignLink[]
): DataSource[] {
  const ingestionIds = new Set(ingestionSources.map((s) => s.crmConfigId));
  const campaignCountByConfig = new Map<string, number>();
  const latestSyncByConfig = new Map<string, string>();
  // Pull CRMs have no ingestion records; their case volume is the sum of their campaigns'
  // totalCases (the same figure the campaigns list shows). Ingestion sources get their own
  // authoritative caseCount from /sources, so this map only feeds the pull branch.
  const caseCountByConfig = new Map<string, number>();
  for (const c of campaigns) {
    if (!c.crmConfigId) continue;
    campaignCountByConfig.set(c.crmConfigId, (campaignCountByConfig.get(c.crmConfigId) ?? 0) + 1);
    caseCountByConfig.set(
      c.crmConfigId,
      (caseCountByConfig.get(c.crmConfigId) ?? 0) + (c.totalCases ?? 0)
    );
    if (c.lastSyncedAt) {
      const prev = latestSyncByConfig.get(c.crmConfigId);
      if (!prev || c.lastSyncedAt > prev) latestSyncByConfig.set(c.crmConfigId, c.lastSyncedAt);
    }
  }

  const pull: DataSource[] = crmConfigs
    .filter((c) => !ingestionIds.has(c.id) && !isIngestionCrmType(c.crmType))
    .map((c) => ({
      capabilities: capabilitiesFor(c.crmType),
      caseCount: caseCountByConfig.get(c.id) ?? 0, // summed from the CRM's campaigns
      createdAt: c.createdAt ?? null,
      crmConfigId: c.id,
      crmType: c.crmType,
      failureCount: 0,
      family: familyFor(c.crmType),
      health: pullHealth(c, latestSyncByConfig.get(c.id) ?? null),
      issueCount: 0,
      mappedCampaignCount: campaignCountByConfig.get(c.id) ?? 0,
      name: c.name ?? pullSourceName(c),
      organizationId: c.organizationId,
      organizationName: c.organizationName ?? "",
      recordCount: 0, // pull CRMs don't track ingestion records
      syncIntervalHours: c.syncIntervalHours ?? null,
    }));

  const ingestion: DataSource[] = ingestionSources.map((s) => {
    const counts = reviewCountsFor(s.crmConfigId, reviewByCampaign);
    return {
      capabilities: capabilitiesFor(s.crmType),
      caseCount: s.caseCount ?? 0,
      createdAt: s.createdAt ?? null,
      crmConfigId: s.crmConfigId,
      crmType: s.crmType,
      failureCount: counts.failures,
      family: familyFor(s.crmType),
      health: ingestionHealth(s),
      issueCount: counts.issues,
      mappedCampaignCount: s.mappedCampaignCount,
      name: s.name,
      organizationId: s.organizationId,
      organizationName: s.organizationName,
      recordCount: s.recordCount ?? 0,
      syncIntervalHours: null,
    };
  });

  // Surface the sources with the most outstanding ISSUES first (failures are a separate
  // notification and don't drive this ordering).
  return [...ingestion, ...pull].sort(
    (a, b) => b.issueCount - a.issueCount || a.name.localeCompare(b.name)
  );
}
